// src/utils/wecom.js
import * as ww from '@wecom/jssdk'
import { showToast } from 'vant'
import requests from './axios'

// 从配置文件导入企业微信配置
import { getWecomConfig,WecomEntryScene } from '../config/wecomConfig'
const { corpId: CORP_ID, agentId: AGENT_ID } = getWecomConfig()

// 存储注册状态
let isRegistered = false

/**
 * 企业微信JSSDK注册（agentConfig）
 * @param {Object|string} signatureData - 签名数据对象或直接的signature字符串
 * @param {string[]} jsApiList - 需要使用的JS接口列表
 * @returns 注册结果Promise
 */
export const registerWecom = async (
  signatureData,
  jsApiList = ['getCurExternalContact', 'sendChatMessage', 'getCurExternalChat', 'getContext'], // 添加getContext到默认接口列表
) => {
  // 1. 严格校验签名数据格式（确保后端返回完整参数）
  if (!signatureData || typeof signatureData !== 'object') {
    showToast('签名数据格式错误，需传入对象类型')
    return Promise.reject(new Error('无效的签名数据类型'))
  }

  // 解构后端返回的参数（注意noncestr是全小写）
  const { signature, noncestr, timestamp, url: backendUrl } = signatureData

  // 校验核心字段是否存在
  const missingFields = []
  if (!signature) missingFields.push('signature')
  if (!noncestr) missingFields.push('noncestr')
  if (timestamp === undefined || timestamp === null) missingFields.push('timestamp')
  if (!backendUrl) missingFields.push('url')

  if (missingFields.length > 0) {
    const errorMsg = `签名数据不完整，缺少字段：${missingFields.join(', ')}`
    showToast(errorMsg)
    return Promise.reject(new Error(errorMsg))
  }

  // 2. 验证当前页面URL与后端签名URL的一致性（关键！）
  const currentUrl = window.location.href.split('#')[0]
  if (backendUrl !== currentUrl) {
    console.warn('⚠️ 后端签名URL与当前页面URL不一致', {
      后端用于签名的URL: backendUrl,
      当前页面实际URL: currentUrl,
    })
    showToast('URL不一致，可能导致签名失败')
    // 可选：如果URL不一致，自动重新获取签名（适用于页面跳转场景）
    // return getWecomSignature().then(newSig => registerWecom(newSig, jsApiList));
  }

  // 3. 执行agentConfig注册
  return new Promise((resolve, reject) => {
    ww.register({
      corpId: CORP_ID,
      agentId: AGENT_ID,
      jsApiList,
      debug: import.meta.env.MODE === 'development', // 开发环境开启调试模式
      getAgentConfigSignature: async () => {
        // 直接使用后端返回的核心参数（确保与生成签名时完全一致）
        return {
          timestamp: String(timestamp), // 强制转为字符串，避免数字类型问题
          nonceStr: noncestr, // 使用后端返回的全小写noncestr
          signature: signature,
          corpId: CORP_ID,
          agentId: AGENT_ID,
        }
      },
      onAgentConfigSuccess: (res) => {
        isRegistered = true
        // showToast('企业微信功能初始化成功')
        resolve({ success: true, data: res })
      },
      onAgentConfigFail: (error) => {
        isRegistered = false
        console.error('❌ agentConfig注册失败', error)
        // 针对常见错误码提供更具体的提示
        const errorTips = {
          40093: '签名无效，请检查参数一致性',
          40013: 'CorpID无效',
          40058: 'AgentID无效',
        }
        const errMsg = errorTips[error.errCode] || error.errMsg || '未知错误'
        showToast(`初始化失败: ${errMsg}`)
        reject(new Error(`agentConfig失败: ${errMsg}`))
      },
    })
  })
}

/**
 * 检查是否已注册
 * @returns {boolean} 注册状态
 */
export const checkRegistered = () => {
  if (!isRegistered) {
    showToast('请先完成企业微信功能初始化')
  }
  return isRegistered
}

/**
 * 获取当前页面打开场景
 * 兼容性：企业微信 3.0.24 开始支持
 * @returns {Promise<Object>} 场景信息
 * @property {string} entry - 进入页面的场景类型（参考WecomEntryScene枚举）
 * @property {string} [shareTicket] - 从设置了withShareTicket: true的消息进入时返回
 * @property {string} errMsg - 错误信息
 * @property {number} errCode - 错误码
 */
export const getWecomContext = async () => {
  if (!checkRegistered()) return Promise.reject(new Error('未注册企业微信JSSDK'))

  try {
    // 调用ww.getContext，兼容Promise方式
    const result = await ww.getContext()

    // 存储场景信息到localStorage（可选）
    localStorage.setItem('wecom_entry_scene', result.entry)
    if (result.shareTicket) {
      localStorage.setItem('wecom_share_ticket', result.shareTicket)
    }

    return result
  } catch (error) {
    console.error('❌ 获取页面场景失败', error)
    const errMsg = error.errMsg || '获取场景信息失败'
    // 针对低版本企业微信的兼容提示
    if (error.errCode === -1) {
      showToast('当前企业微信版本过低，不支持场景检测功能')
    } else {
      showToast(`场景获取失败: ${errMsg}`)
    }
    return Promise.reject(error)
  }
}

/**
 * 获取场景类型的中文描述
 * @param {string} entry - 场景类型标识
 * @returns {string} 中文描述
 */
export const getEntrySceneDesc = (entry) => {
  const sceneMap = {
    [WecomEntryScene.CONTACT_PROFILE]: '从联系人详情进入',
    [WecomEntryScene.SINGLE_CHAT_TOOLS]: '从单聊会话的工具栏进入',
    [WecomEntryScene.GROUP_CHAT_TOOLS]: '从群聊会话的工具栏进入',
    [WecomEntryScene.CHAT_ATTACHMENT]: '从会话的聊天附件栏进入',
    [WecomEntryScene.SINGLE_KF_TOOLS]: '从微信客服的工具栏进入',
    [WecomEntryScene.CHAIN_SINGLE_CHAT_TOOLS]: '上下游单聊会话的工具栏',
    [WecomEntryScene.CHAIN_GROUP_CHAT_TOOLS]: '上下游群聊会话的工具栏',
    [WecomEntryScene.INTERNAL_GROUP_CHAT_BOARD]: '从内部群群看板进入',
    [WecomEntryScene.NORMAL]: '通用场景（工作台、聊天会话等）',
  }
  return sceneMap[entry] || '未知场景'
}

/**
 * 检查是否在客户相关场景中（可调用客户联系相关接口）
 * @returns {Promise<boolean>} 是否在客户场景中
 */
export const isInCustomerScene = async () => {
  try {
    const context = await getWecomContext()
    // 客户相关场景列表
    const customerScenes = [
      WecomEntryScene.CONTACT_PROFILE,
      WecomEntryScene.SINGLE_CHAT_TOOLS,
      WecomEntryScene.GROUP_CHAT_TOOLS,
      WecomEntryScene.CHAT_ATTACHMENT,
      WecomEntryScene.SINGLE_KF_TOOLS,
      WecomEntryScene.CHAIN_SINGLE_CHAT_TOOLS,
      WecomEntryScene.CHAIN_GROUP_CHAT_TOOLS,
    ]
    const isCustomerScene = customerScenes.includes(context.entry)
    return isCustomerScene
  } catch (error) {
    console.error('检查客户场景失败', error)
    return false
  }
}

/**
 * 获取当前外部联系人信息
 * @returns 联系人信息Promise
 */
export const getCurrentExternalContact = async () => {
  // 先检查是否在支持的场景中
  const isCustomerScene = await isInCustomerScene()
  if (!isCustomerScene) {
    showToast('当前场景不支持获取外部联系人信息')
    return Promise.reject(new Error('不支持的场景'))
  }

  if (!checkRegistered()) return Promise.reject(new Error('未注册'))

  try {
    const result = await ww.getCurExternalContact()
    localStorage.setItem('wecom_external_userid', result.userId)
    return result
  } catch (error) {
    console.error('获取外部联系人失败', error)
    // showToast(`获取联系人失败: ${error.message}`)
    localStorage.removeItem('wecom_external_userid')
    return Promise.reject(error)
  }
}

/**
 * 获取客户群的群ID
 * @returns 客户群的群IDPromise
 */
export const getCurExternalChat = async () => {
  // 先检查是否在群聊相关场景中
  const context = await getWecomContext()
  const groupScenes = [
    WecomEntryScene.GROUP_CHAT_TOOLS,
    WecomEntryScene.CHAIN_GROUP_CHAT_TOOLS,
    WecomEntryScene.CHAT_ATTACHMENT,
  ]
  if (!groupScenes.includes(context.entry)) {
    showToast('当前场景不支持获取客户群信息')
    return Promise.reject(new Error('不支持的场景'))
  }

  if (!checkRegistered()) return Promise.reject(new Error('未注册'))

  try {
    const result = await ww.getCurExternalChat()
    localStorage.setItem('wecom_external_userid', result.chatId)
    return result
  } catch (error) {
    console.error('获取当前客户群Id失败', error)
    // 针对权限错误的特殊处理
    localStorage.removeItem('wecom_external_userid')
    return Promise.reject(error)
  }
}

/**
 * 发送聊天消息
 * @param {Object} message - 消息内容
 * @param {string} [externalUserid] - 外部联系人ID（可选）
 * @returns 发送结果Promise
 */
export const sendChatMessage = async (message, externalUserid, successCallBack) => {
  // 先检查是否在聊天相关场景中
  const isCustomerScene = await isInCustomerScene()
  if (!isCustomerScene) {
    showToast('当前场景不支持发送聊天消息')
    return Promise.reject(new Error('不支持的场景'))
  }

  if (!checkRegistered()) return Promise.reject(new Error('未注册'))
  if (!message || !message.msgtype) {
    showToast('请传入有效的消息内容')
    return Promise.reject(new Error('无效的消息内容'))
  }

  // 包装成Promise，兼容官方回调机制
  return new Promise((resolve, reject) => {
    const params = {
      ...message,
      ...(externalUserid && { externalUserid }),
      enterChat: false,
      // 成功回调：优先执行外部传入的successCallBack，再resolve
      success: (res) => {
        successCallBack?.(res) // 执行外部传入的成功回调
        showToast('消息发送成功')
        resolve(res)
      },
      // 失败回调：reject错误信息
      fail: (err) => {
        reject(err)
      },
      // 取消回调：用户主动取消发送时触发（官方文档规范）
      cancel: () => {
        reject(new Error('用户取消发送'))
      },
      // 完成回调：无论成功/失败/取消都会触发（可选）
      complete: (res) => {
      },
    }

    // 调用企业微信发送接口
    ww.sendChatMessage(params)
  })
}

/**
 * 动态获取企业微信应用签名
 * @param {string} url - 当前页面URL，用于生成签名
 * @returns 签名信息Promise
 */
export const getWecomSignature = async (url = window.location.href) => {
  try {
    // 获取完整URL但去除hash部分，这是企业微信签名的要求
    const currentUrl = url.split('#')[0]
    const response = await requests.post('/api/v1/auth/jsapi', {
      url: currentUrl,
    })


    // 检查响应数据格式
    if (!response.data || !response.data.signature) {
      throw new Error('签名数据格式错误')
    }

    // 保存使用的URL，确保后续使用相同URL进行签名
    response.data.originalUrl = currentUrl
    return response.data
  } catch (error) {
    console.error('获取企业微信应用签名失败:', error)
    // showToast(`获取签名失败: ${error.message || '未知错误'}`)
    throw error
  }
}

/**
 * 自动初始化企业微信JSSDK
 * 自动获取签名并完成注册
 * @param {string[]} jsApiList - 需要使用的JS接口列表
 * @returns 注册结果Promise
 */
export const autoInitWecom = async (
  jsApiList = ['getCurExternalContact', 'sendChatMessage', 'getCurExternalChat', 'getContext'], // 添加getContext到默认接口列表
) => {
  try {
    // 动态获取签名
    const signatureData = await getWecomSignature()

    // 将完整的签名数据传递给registerWecom，而不仅仅是signature
    const initResult = await registerWecom(signatureData, jsApiList)

    // 初始化成功后自动获取场景信息（可选）
    await getWecomContext().catch((err) => {
      console.warn('初始化后获取场景信息失败（非必需功能，不影响主流程）', err)
    })

    return initResult
  } catch (error) {
    console.error('自动初始化企业微信JSSDK失败:', error)
    throw error
  }
}

// 导出常用配置和新增的场景相关方法
export const wecomConfig = {
  CORP_ID,
  AGENT_ID,
  WecomEntryScene, // 导出场景枚举
}

// 导出所有方法
export default {
  registerWecom,
  checkRegistered,
  getWecomContext,
  getEntrySceneDesc,
  isInCustomerScene,
  getCurrentExternalContact,
  getCurExternalChat,
  sendChatMessage,
  getWecomSignature,
  autoInitWecom,
  wecomConfig,
}
